import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// OCR詳細のルーペ（拡大表示）ON/OFF状態を保存する localStorage キー。
// 読取パターンごとに記憶するため patternId をキーに含める（#3079）。
const MAGNIFIER_ENABLED_KEY_PREFIX = 'ocrMagnifier';
// patternId 未指定時のフォールバックキー（従来キー互換）
const MAGNIFIER_ENABLED_LEGACY_KEY = 'ocrMagnifierEnabled';

// 自由読取結果は特定パターンに属さないため、専用の識別子でルーペ状態を1つ記憶する（#3079）
export const FREE_AI_OCR_MAGNIFIER_ID = 'freeAiOcr';

const buildStorageKey = (patternId?: string): string =>
  patternId ? `${MAGNIFIER_ENABLED_KEY_PREFIX}_${patternId}` : MAGNIFIER_ENABLED_LEGACY_KEY;

// localStorage から保存値を取得（未設定時は ON）
const getStoredMagnifierEnabled = (storageKey: string): boolean => {
  try {
    const stored = localStorage.getItem(storageKey);
    if (stored !== null) {
      return JSON.parse(stored) as boolean;
    }
  } catch {
    // localStorage が使用できない場合やパースエラーは無視
  }
  return true;
};

/**
 * OCR詳細のルーペ（拡大表示）ON/OFF状態を localStorage に永続化して管理するフック。
 * 読取パターンごと（patternId 単位）に記憶し、パターンを跨ぐと独立する（#3079）。
 * localStorage はユーザーの端末（ブラウザ）単位のため、個人設定として機能する。
 * 未設定時（初回）は ON。
 */
export const useMagnifierEnabled = (
  patternId?: string,
): {
  magnifierEnabled: boolean;
  toggleMagnifier: () => void;
} => {
  const storageKey = useMemo(() => buildStorageKey(patternId), [patternId]);
  const [magnifierEnabled, setMagnifierEnabled] = useState<boolean>(() =>
    getStoredMagnifierEnabled(storageKey),
  );

  // 直近で同期済みのキーを保持し、キー変更（パターン切替）と値変更（トグル）を区別する
  const syncedKeyRef = useRef<string>(storageKey);

  useEffect(() => {
    if (syncedKeyRef.current !== storageKey) {
      // パターン切替: 新しいパターンの保存値を読み込む（このタイミングでは保存しない）
      syncedKeyRef.current = storageKey;
      setMagnifierEnabled(getStoredMagnifierEnabled(storageKey));
      return;
    }
    // 同一パターン内での状態変更: 現在のパターンのキーに保存する
    try {
      localStorage.setItem(storageKey, JSON.stringify(magnifierEnabled));
    } catch {
      // localStorage が使用できない場合は無視
    }
  }, [storageKey, magnifierEnabled]);

  const toggleMagnifier = useCallback((): void => {
    setMagnifierEnabled(prev => !prev);
  }, []);

  return { magnifierEnabled, toggleMagnifier };
};
